import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contextStore/AuthContext';
import { getTransactionsForBuyer, getTransactionsForSeller, confirmDelivery, cancelTransaction } from '../../firebase/collections';
import { formatPrice, formatDate } from '../../utils/formatters';
import { formatTokenAmount, getTokenConfigFor } from '../../utils/tokenConfig';
import { ToastContext } from '../../contextStore/ToastContext';
import './Dashboard.css';

function Transactions() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { addToast } = useContext(ToastContext);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('buying');
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = tab === 'buying'
      ? getTransactionsForBuyer(user.uid, 50)
      : getTransactionsForSeller(user.uid, 50);
    q.get()
      .then((snap) => {
        setTransactions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .finally(() => setLoading(false));
  }, [user?.uid, tab]);

  if (!user) {
    return <p>{t('transactions.pleaseLogIn')}</p>;
  }

  return (
    <div className="dashboardTransactions">
      <h1>{t('transactions.title')}</h1>
      <div className="dashboardTransactionsTabs">
        <button
          type="button"
          className={tab === 'buying' ? 'active' : ''}
          onClick={() => setTab('buying')}
        >
          {t('transactions.purchases')}
        </button>
        <button
          type="button"
          className={tab === 'selling' ? 'active' : ''}
          onClick={() => setTab('selling')}
        >
          {t('transactions.sales')}
        </button>
      </div>
      {loading ? (
        <p>{t('common.loading')}</p>
      ) : transactions.length === 0 ? (
        <p className="dashboardTransactionsEmpty">{t('transactions.noTransactions')}</p>
      ) : (
        <ul className="dashboardTransactionsList">
          {transactions.map((transaction) => (
            <li key={transaction.id} className="dashboardTransactionsItem">
              <div className="dashboardTransactionsItemImage">
                {transaction.productImage ? (
                  <img src={transaction.productImage} alt="" />
                ) : (
                  <span>{t('common.ad')}</span>
                )}
              </div>
              <div className="dashboardTransactionsItemBody">
                <Link to={`/ad/${transaction.productId}`} className="dashboardTransactionsItemTitle">
                  {transaction.productName}
                </Link>
                <p className="dashboardTransactionsItemMeta">
                  {tab === 'buying'
                    ? `${t('transactions.seller')}: ${transaction.sellerName}`
                    : `${t('transactions.buyer')}: ${transaction.buyerName}`}
                </p>
                <p className="dashboardTransactionsItemAmount">
                  {formatTokenAmount(transaction.amount, getTokenConfigFor(transaction.tokenKind))} {getTokenConfigFor(transaction.tokenKind).name}
                </p>
                {transaction.commissionAmount > 0 && (
                  <p className="dashboardTransactionsItemCommission">
                    {t('transactions.commissionApplied', { amount: formatTokenAmount(transaction.commissionAmount, getTokenConfigFor(transaction.tokenKind)) })}
                  </p>
                )}
                <span className={`dashboardTransactionsItemStatus dashboardTransactionsItemStatus--${transaction.status}`}>
                  {transaction.escrowStatus === 'held' && tab === 'buying'
                    ? t('transactions.escrowHeld')
                    : t(`transactions.${transaction.status}`)}
                </span>
                {/* Escrow: buyer can confirm delivery when payment is held */}
                {transaction.escrowStatus === 'held' && tab === 'buying' && (
                  <p className="dashboardTransactionsItemEscrowHint">{t('transactions.paymentLocked')}</p>
                )}
                {transaction.escrowStatus === 'held' && tab === 'buying' && (
                  <button
                    type="button"
                    className="dashboardTransactionsItemAction"
                    disabled={actionLoading[transaction.id]}
                    onClick={() => {
                      setActionLoading((p) => ({ ...p, [transaction.id]: true }));
                      confirmDelivery(transaction.id)
                        .then(() => {
                          addToast(t('transactions.deliveryConfirmed'), 'success');
                          setTransactions((prev) =>
                            prev.map((tx) =>
                              tx.id === transaction.id
                                ? { ...tx, status: 'completed', escrowStatus: 'released', deliveryStatus: 'confirmed' }
                                : tx
                            )
                          );
                        })
                        .catch(() => addToast(t('auth.somethingWentWrong'), 'error'))
                        .finally(() => setActionLoading((p) => ({ ...p, [transaction.id]: false })));
                    }}
                  >
                    {actionLoading[transaction.id] ? t('common.loading') : t('transactions.confirmDelivery')}
                  </button>
                )}
                {/* Cancel option when payment not yet sent */}
                {transaction.status === 'initiated' && (
                  <button
                    type="button"
                    className="dashboardTransactionsItemCancel"
                    disabled={actionLoading[transaction.id]}
                    onClick={() => {
                      setActionLoading((p) => ({ ...p, [transaction.id]: true }));
                      cancelTransaction(transaction.id)
                        .then(() => {
                          setTransactions((prev) =>
                            prev.map((tx) =>
                              tx.id === transaction.id ? { ...tx, status: 'cancelled' } : tx
                            )
                          );
                        })
                        .catch(() => addToast(t('auth.somethingWentWrong'), 'error'))
                        .finally(() => setActionLoading((p) => ({ ...p, [transaction.id]: false })));
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                )}
                {transaction.createdAt?.toDate && (
                  <span className="dashboardTransactionsItemDate">
                    {formatDate(transaction.createdAt.toDate())}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default Transactions;